import { Stack } from '@cmg/design-system';
import defaultTo from 'lodash/defaultTo';
import React from 'react';

import { InstitutionalIndicationOrderType } from '../../../../../../../../graphql';
import { useIndicationActivityBuySideContext } from '../../../../context/IndicationActivityBuySideContext';
import IndicationSectionPassedReadContent from '../../common/indication-section-passed-read-content/IndicationSectionPassedReadContent';
import IndicationSectionReadContent from '../../common/indication-section-read-content/IndicationSectionReadContent';
import { IndicationSection } from '../../common/indication-section-read-view/IndicationSection';
import ReadButtons from '../../common/indication-section-read-view/ReadButtons';
import IndicationStatus from '../../common/indication-status/IndicationStatus';
import useCreateBuySideReadButtonProps from './hooks/useCreateBuySideReadButtonProps';

export type Props = Readonly<{
  onStartEditing: () => void;
  hasAllocationRanking: boolean;
}>;

const EMPTY_ARRAY = [];
const IndicationSectionReadView: React.FC<Props> = ({ onStartEditing, hasAllocationRanking }) => {
  const {
    basicPublishedOffering,
    orderBookConfiguration,
    publishedOffering,
    complianceWarning: { hasComplianceWarning },
  } = useIndicationActivityBuySideContext();

  const offering = basicPublishedOffering!;
  const indication = offering.myInstitutionalIndication!;

  const { readButtonDialogs, ...readButtonProps } = useCreateBuySideReadButtonProps({
    onStartEditing,
    hasAllocationRanking,
  });

  return (
    <IndicationSection
      sectionProps={{
        titleItems: <IndicationStatus status={indication.status} />,
        actions: [
          <ReadButtons
            {...readButtonProps}
            hasComplianceWarning={hasComplianceWarning}
            key="read-buttons"
          />,
        ],
      }}
    >
      <Stack gap={1}>
        {indication.type === InstitutionalIndicationOrderType.Pass && (
          <IndicationSectionPassedReadContent submittedOn={indication.auditInfo.createdAt} />
        )}
        {indication.type !== InstitutionalIndicationOrderType.Pass && (
          <IndicationSectionReadContent
            offeringType={offering.type}
            isIndicationPriceAsPercentageParEnabled={
              orderBookConfiguration.isIndicationPriceAsPercentageParEnabled
            }
            securityType={defaultTo(publishedOffering?.security.type, null)}
            filings={defaultTo(publishedOffering?.terms.filings, null)}
            issuerName={defaultTo(publishedOffering?.issuer.name, null)}
            submittedOn={indication.auditInfo.createdAt}
            indicationTrancheId={indication.trancheId}
            indicationInstrumentId={indication.instrumentId}
            demandCurrencyCode={indication.currencyCode}
            acknowledgements={indication.acknowledgements}
            indicationVersion={indication.version}
            bndAgentCmgEntityKey={indication.billingAndDeliveryAgentCmgEntityKey}
            interestLevels={indication.interestLevels}
            tranches={defaultTo(offering.orderEntryProfile?.tranches, EMPTY_ARRAY)}
            demandInstruments={defaultTo(publishedOffering?.instruments, null)}
            managers={offering.syndicate.managers}
            securityTypeDisplayName={offering.securityTypeDisplayName!}
          />
        )}

        {...readButtonDialogs}
      </Stack>
    </IndicationSection>
  );
};

export default IndicationSectionReadView;
